// =====================================================================
// KEYBOARD PRODUCTIVITY — vim-style leader nav + recents + favorites
// =====================================================================
// Leader key:  g  then…
//   g l → list of current doctype      g h → home / workspaces
//   g n → new document (current type)  g r → Recents panel
//   g f → Favorites panel              g s → focus the global search
//
// • Only fires when NOT typing and no modifier is held and no Frappe
//   dialog / command-palette is open → never collides with Frappe's own
//   shortcuts or text input.
// • Panels are body-appended + position:fixed (proven safe pattern).
// • Recents auto-tracked from frappe.router; Favorites persisted in
//   localStorage. Gated by uxPrefs.keyboard. Fully defensive.
// =====================================================================

import { uxPrefs } from "./ux_prefs.js";

const REC_KEY = "e16:recents";
const FAV_KEY = "e16:favs";
const MAX_REC = 15;
const LEADER = "g";
const SEQ_MS = 1200;

function lang() {
	return document.documentElement.getAttribute("lang") === "ar" ? "ar" : "en";
}
function L(ar, en) { return lang() === "ar" ? ar : en; }

function readArr(key) {
	try { return JSON.parse(localStorage.getItem(key) || "[]") || []; }
	catch (e) { return []; }
}
function writeArr(key, arr) {
	try { localStorage.setItem(key, JSON.stringify(arr)); } catch (e) {}
}

// ---------- route helpers -------------------------------------------

function curRoute() {
	try { return frappe.get_route ? frappe.get_route() : []; }
	catch (e) { return []; }
}

function curDoctype() {
	const r = curRoute();
	if (!r || !r.length) return null;
	if (["Form", "List", "Tree", "report", "print", "dashboard-view"]
		.includes(r[0]) && r[1]) return r[1];
	return null;
}

function routeKey(r) {
	try { return "#" + (Array.isArray(r) ? r.join("/") : String(r)); }
	catch (e) { return "#"; }
}

function routeTitle(r) {
	if (!Array.isArray(r) || !r.length) return L("الرئيسية", "Home");
	if (r[0] === "Form") return r[1] + " · " + (r[2] || "");
	if (r[0] === "List") return L(r[1] + " (قائمة)", r[1] + " (List)");
	if (r[0] === "Workspaces" || r[0] === "workspace") return r[1] || L("مساحة عمل", "Workspace");
	if (r[0] === "query-report" || r[0] === "report") return (r[1] || "") + L(" (تقرير)", " (Report)");
	return r.join(" / ");
}

function go(route) {
	try {
		if (Array.isArray(route)) frappe.set_route(...route);
		else frappe.set_route(route);
	} catch (e) {
		try { window.location.hash = routeKey(route); } catch (e2) {}
	}
}

// ---------- recents tracking ----------------------------------------

function trackRoute() {
	const r = curRoute();
	if (!r || !r.length) return;
	if (["e16", "modal"].includes(r[0])) return;
	const key = routeKey(r);
	let rec = readArr(REC_KEY).filter((x) => x.key !== key);
	rec.unshift({ key, route: r, title: routeTitle(r), ts: Date.now() });
	if (rec.length > MAX_REC) rec = rec.slice(0, MAX_REC);
	writeArr(REC_KEY, rec);
}

// ---------- panel UI -------------------------------------------------

let backdrop = null;
let panel = null;

function closePanel() {
	if (panel) { panel.classList.remove("is-on"); }
	if (backdrop) { backdrop.classList.remove("is-on"); }
	setTimeout(() => {
		if (panel && panel.parentNode) panel.parentNode.removeChild(panel);
		if (backdrop && backdrop.parentNode) backdrop.parentNode.removeChild(backdrop);
		panel = null; backdrop = null;
	}, 200);
}

function openPanel(kind) {
	if (panel) closePanel();

	backdrop = document.createElement("div");
	backdrop.className = "e16-panel-backdrop";
	backdrop.addEventListener("click", closePanel);

	panel = document.createElement("div");
	panel.className = "e16-panel";

	const isFav = kind === "fav";
	const title = isFav ? L("المفضّلة", "Favorites") : L("الأخيرة", "Recents");
	const hint = isFav
		? L("اضغط g ثم f للفتح · ★ للتثبيت/الإزالة", "Press g then f · ★ to pin/unpin")
		: L("اضغط g ثم r للفتح", "Press g then r to open");

	let rows = "";
	const favs = readArr(FAV_KEY);
	const favKeys = new Set(favs.map((f) => f.key));

	if (isFav) {
		// Allow pinning the CURRENT route too
		const cr = curRoute();
		const ck = routeKey(cr);
		const list = favs.slice();
		if (cr && cr.length && !favKeys.has(ck)) {
			list.unshift({ key: ck, route: cr, title: routeTitle(cr), _now: 1 });
		}
		if (!list.length) {
			rows = '<div class="e16-panel__empty">' +
				L("لا توجد مفضّلات بعد", "No favorites yet") + "</div>";
		} else {
			list.forEach((it) => {
				const pinned = favKeys.has(it.key);
				rows +=
					'<div class="e16-panel__item" data-go=\'' +
					escAttr(JSON.stringify(it.route)) + "'>" +
					"<span>" + esc(it.title) +
					(it._now ? ' <span class="e16-panel__meta">' +
						L("(الحالية)", "(current)") + "</span>" : "") +
					"</span>" +
					'<button class="e16-panel__pin' + (pinned ? " is-pinned" : "") +
					'" data-pin=\'' + escAttr(JSON.stringify(it)) + "'>" +
					(pinned ? "★" : "☆") + "</button>" +
					"</div>";
			});
		}
	} else {
		const rec = readArr(REC_KEY);
		if (!rec.length) {
			rows = '<div class="e16-panel__empty">' +
				L("لا يوجد سجل بعد", "Nothing visited yet") + "</div>";
		} else {
			rec.forEach((it) => {
				rows +=
					'<div class="e16-panel__item" data-go=\'' +
					escAttr(JSON.stringify(it.route)) + "'>" +
					"<span>" + esc(it.title) + "</span>" +
					'<span class="e16-panel__meta">' + timeAgo(it.ts) + "</span>" +
					"</div>";
			});
		}
	}

	panel.innerHTML =
		'<div class="e16-panel__title">' + (isFav ? "★ " : "🕑 ") + esc(title) +
		"</div>" +
		'<div class="e16-panel__hint">' + esc(hint) + "</div>" + rows;

	document.body.appendChild(backdrop);
	document.body.appendChild(panel);
	requestAnimationFrame(() => {
		backdrop.classList.add("is-on");
		panel.classList.add("is-on");
	});

	panel.querySelectorAll(".e16-panel__item").forEach((row) => {
		row.addEventListener("click", (ev) => {
			if (ev.target.closest(".e16-panel__pin")) return;
			try {
				const route = JSON.parse(row.getAttribute("data-go"));
				closePanel();
				go(route);
			} catch (e) {}
		});
	});
	panel.querySelectorAll(".e16-panel__pin").forEach((btn) => {
		btn.addEventListener("click", (ev) => {
			ev.stopPropagation();
			try {
				const it = JSON.parse(btn.getAttribute("data-pin"));
				let f = readArr(FAV_KEY);
				if (f.some((x) => x.key === it.key)) {
					f = f.filter((x) => x.key !== it.key);
				} else {
					delete it._now;
					f.unshift(it);
				}
				writeArr(FAV_KEY, f);
				openPanel("fav"); // re-render
			} catch (e) {}
		});
	});
}

function esc(s) {
	return String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({
		"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
	}[c]));
}
function escAttr(s) { return esc(s).replace(/'/g, "&#39;"); }

function timeAgo(ts) {
	const s = Math.floor((Date.now() - ts) / 1000);
	if (s < 60) return L("الآن", "now");
	if (s < 3600) return L(Math.floor(s / 60) + "د", Math.floor(s / 60) + "m");
	if (s < 86400) return L(Math.floor(s / 3600) + "س", Math.floor(s / 3600) + "h");
	return L(Math.floor(s / 86400) + "ي", Math.floor(s / 86400) + "d");
}

// ---------- keyboard plumbing ---------------------------------------

function isTyping(t) {
	if (!t) return false;
	const tag = (t.tagName || "").toLowerCase();
	if (tag === "input" || tag === "textarea" || tag === "select") return true;
	if (t.isContentEditable) return true;
	if (t.closest && t.closest(".ace_editor, .CodeMirror, [contenteditable='true']"))
		return true;
	return false;
}

function dialogOpen() {
	return !!document.querySelector(
		".modal.show, .e16-cmdk.is-on, .modal.in, .e16-lab.is-on, .e16-panel"
	);
}

let leaderAt = 0;

function onKey(e) {
	if (!uxPrefs.get().keyboard) return;
	if (e.ctrlKey || e.metaKey || e.altKey) return;
	if (isTyping(e.target)) return;

	if (e.key === "Escape" && panel) {
		closePanel();
		return;
	}

	const now = Date.now();
	const k = (e.key || "").toLowerCase();

	if (now - leaderAt < SEQ_MS && leaderAt > 0) {
		leaderAt = 0;
		const dt = curDoctype();
		if (k === "l") {
			if (dt) { e.preventDefault(); go(["List", dt]); }
		} else if (k === "h") {
			e.preventDefault();
			go("");
		} else if (k === "n") {
			if (dt) {
				e.preventDefault();
				try { frappe.new_doc(dt); } catch (er) { go(["Form", dt, "new"]); }
			}
		} else if (k === "r") {
			e.preventDefault();
			openPanel("rec");
		} else if (k === "f") {
			e.preventDefault();
			openPanel("fav");
		} else if (k === "s") {
			e.preventDefault();
			try {
				const sb = document.querySelector(
					"#navbar-search, .search-bar input, input[data-element='search']"
				);
				if (sb) sb.focus();
				else if (window.frappe && frappe.search) frappe.search.show();
			} catch (er) {}
		}
		return;
	}

	if (k === LEADER && !dialogOpen()) {
		leaderAt = now;
	} else {
		leaderAt = 0;
	}
}

let mounted = false;

export const productivity = {
	mount() {
		if (mounted) return;
		mounted = true;

		document.addEventListener("keydown", onKey, true);

		// Track route history
		const t = () => setTimeout(trackRoute, 60);
		try {
			if (window.frappe && frappe.router && frappe.router.on) {
				frappe.router.on("change", t);
			}
		} catch (e) {}
		// initial
		t();

		document.addEventListener("e16:ux-applied", () => {
			if (!uxPrefs.get().keyboard && panel) closePanel();
		});
	},
};
